import React, { useCallback, useRef } from 'react';

import { ScrollView } from 'devextreme-react';
import Splitter, { Item } from 'devextreme-react/splitter';
import { formatMessage } from 'devextreme/localization';

import FilterBuilderInline, { DeclaredParameter } from '../filter-selection/FilterBuilderInline';
import { collectParameterOptions } from '../filter-selection/FilterFieldsShared';
import IterationDatasetPanel from './IterationDatasetPanel';
import useFilteringCapabilities from './useFilteringCapabilities';
import GroupFilterDto from '../../classes/dtos/datasourcemodule/GroupFilterDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataTableParametersDto from '../../classes/dtos/pathcommonmodule/DataTableParametersDto';
import DataTableIterationLinkDto from '../../classes/dtos/pathcommonmodule/DataTableIterationLinkDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';

interface FiltersSelectionProps {
    TableMapping: DataTableReadDto | null;
    SetTableMapping(value: DataTableReadDto): void;
    WithSimpleParameters: boolean;
    // Variables nommées dans les grilles de mapping (colonne FILTER, mode complexe) : sélectionnables
    // dans le FilterBuilder en plus de celles déjà présentes dans l'arbre.
    DeclaredParameters?: DeclaredParameter[];
    // FieldReferenceIds des lignes de mapping cochées : le FilterBuilder ne propose que ces champs.
    SelectedFieldReferenceIds?: number[];
}

const PANEL_HEIGHT = 'calc(100vh - 320px)';
const mutedTextStyle: React.CSSProperties = { color: '#9393A2', fontSize: 14, fontFamily: 'Manrope', fontWeight: '500' };

// Onglet "Filters", partagé par tous les types de path (reporting/AI/metaapi/synchro/datatable) : un
// FilterBuilder unique (conditions Constante + Variable mélangées). Les variables ne sont plus définies
// ici (l'ancien panneau de droite a été retiré) : elles sont créées dans les grilles de mapping, et le
// FilterBuilder propose celles déjà référencées par le filtre courant. Filter et Iteration vivent sous
// TableMapping.Parameters (DataTableParametersDto) : on lit/écrit directement depuis/vers TableMapping.
const FiltersSelection: React.FC<FiltersSelectionProps> = ({ TableMapping, SetTableMapping, DeclaredParameters, SelectedFieldReferenceIds }) => {

    const tableMappingWithFilters = TableMapping instanceof DataSourceDataTableReadDto ? TableMapping : null;

    const TableReferenceId = tableMappingWithFilters?.TableReferenceId ?? 0;
    const Filter = tableMappingWithFilters?.Parameters?.Filter ?? null;
    const Iteration = tableMappingWithFilters?.Parameters?.Iteration ?? null;
    const IterationLinks = tableMappingWithFilters?.Parameters?.IterationLinks ?? [];

    // Instantané vivant de la source + de ses paramètres, réécrit à chaque rendu. handleFilterChange et
    // handleParametersChange reconstruisent tous deux Parameters entièrement ; sans lire l'état le plus
    // récent ici, celui qui part d'une closure périmée écrase la modif de l'autre (ex. un lien d'itération
    // fraîchement ajouté effacé par une édition de filtre qui suit).
    const liveRef = useRef(tableMappingWithFilters);
    liveRef.current = tableMappingWithFilters;

    const { withFilter, capabilitiesLoaded } = useFilteringCapabilities(TableReferenceId);

    // Recopie l'identité de la source (hors Parameters) dans un DataTableReadDto neuf.
    const cloneSource = (src: DataSourceDataTableReadDto) => new DataSourceDataTableReadDto(
        src.DataSourceId,
        src.DataSourceLabel,
        src.DataSourceTypeId,
        src.DataSourceTypeLabel,
        src.DatabaseReferenceId,
        src.DatabaseReferenceLabel,
        src.TableReferenceId,
        src.TableReferenceLabel);

    const handleFilterChange = useCallback((filter: GroupFilterDto | null) => {
        const live = liveRef.current;
        if (live === null) {
            return;
        }

        // Prune des liens d'itération « cassés » : un lien dont le paramètre n'est plus une variable du
        // filtre est supprimé dès l'édition du filtre. On lit les liens dans l'état LE PLUS RÉCENT
        // (liveRef), pas dans une closure : un lien ajouté juste avant ne doit pas être perdu.
        const filterVariableNames = new Set(collectParameterOptions(filter).map((o) => o.TechnicalName));
        const prunedIterationLinks = (live.Parameters?.IterationLinks ?? [])
            .filter((l) => filterVariableNames.has(l.ParameterTechnicalName));

        const updatedParameters = new DataTableParametersDto();
        updatedParameters.Filter = filter;
        updatedParameters.Iteration = live.Parameters?.Iteration ?? null;
        updatedParameters.IterationLinks = prunedIterationLinks;

        const updated = cloneSource(live);
        updated.Parameters = updatedParameters;

        SetTableMapping(updated);
    }, [SetTableMapping]);

    // Miroir de handleFilterChange pour le panneau d'itération : préserve le filtre LE PLUS RÉCENT
    // (liveRef), pose Iteration + IterationLinks.
    const handleParametersChange = useCallback((iteration: DataTableReadDto | null, iterationLinks: DataTableIterationLinkDto[]) => {
        const live = liveRef.current;
        if (live === null) {
            return;
        }

        const updatedParameters = new DataTableParametersDto();
        updatedParameters.Filter = live.Parameters?.Filter ?? null;
        updatedParameters.Iteration = iteration;
        updatedParameters.IterationLinks = iterationLinks;

        const updated = cloneSource(live);
        updated.Parameters = updatedParameters;

        SetTableMapping(updated);
    }, [SetTableMapping]);

    const showIteration = capabilitiesLoaded && TableReferenceId > 0 && withFilter;

    const filterContent = (
        <div style={{ flexDirection: 'column', gap: 12, display: 'flex', minWidth: 0 }}>
            {!capabilitiesLoaded ? "" :
                TableReferenceId <= 0 ?
                    (<div style={mutedTextStyle}>{formatMessage("FiltersSelection_NoTableSelected")}</div>) :
                    !withFilter ?
                        (<div style={mutedTextStyle}>{formatMessage("FiltersSelection_NoFilterAvailable")}</div>) :
                        (<FilterBuilderInline TableReferenceId={TableReferenceId} Filter={Filter} SetFilter={handleFilterChange} DeclaredParameters={DeclaredParameters} SelectedFieldReferenceIds={SelectedFieldReferenceIds} />)}
        </div>
    );

    // Splitter DevExtreme entre le filtre (gauche, redimensionnable, pas de taille figée) et le panneau
    // d'itération (droite, redimensionnable + repliable). Chaque volet a son propre ScrollView interne.
    // Structure TOUJOURS identique : le volet d'itération est seulement masqué (visible) tant qu'il n'y a
    // pas de jeu de données d'itération possible. Basculer entre deux arbres différents (ScrollView seul /
    // Splitter) démontait le FilterBuilder dès que les capacités de la source étaient chargées.
    return (
        <Splitter style={{ width: '100%', height: PANEL_HEIGHT }}>
            <Item resizable={true} minSize="360px">
                <ScrollView width="100%" height="100%">
                    <div style={{ padding: '20px' }}>
                        {filterContent}
                    </div>
                </ScrollView>
            </Item>
            <Item visible={showIteration} resizable={true} collapsible={true} size="480px" minSize="380px" maxSize="760px">
                <ScrollView width="100%" height="100%">
                    <div style={{ padding: '20px' }}>
                        {showIteration && <IterationDatasetPanel TableReferenceId={TableReferenceId} Filter={Filter} Iteration={Iteration} IterationLinks={IterationLinks} OnChange={handleParametersChange} />}
                    </div>
                </ScrollView>
            </Item>
        </Splitter>
    );
}

export default FiltersSelection;
